import { useState, useMemo, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useContent } from '../hooks/useContent';
import { useEditorPreview } from '../hooks/useEditorPreview';
import { useToast } from '../hooks/useToast';
import { getContentId } from '../types';
import { api } from '../api/client';
import SectionToggle from '../components/SectionToggle';
import DraggableList from '../components/DraggableList';
import '../styles/editor.css';

interface BulkEdits {
  [contentKey: string]: {
    price?: string;
    category?: string;
    featured?: string;
  };
}

export default function ProductList() {
  const { siteId } = useParams<{ siteId: string }>();
  const { items, loading, error, remove, reorder, refresh } = useContent(siteId, 'product');
  const { items: categoryItems } = useContent(siteId, 'category');
  const { addToast } = useToast();
  const navigate = useNavigate();
  useEditorPreview('/products');
  const [filterCategory, setFilterCategory] = useState<string>('');
  const [bulkEditMode, setBulkEditMode] = useState(false);
  const [bulkEdits, setBulkEdits] = useState<BulkEdits>({});
  const [saving, setSaving] = useState(false);
  const [saveProgress, setSaveProgress] = useState({ current: 0, total: 0 });

  // Build category list with product counts
  const categoryOptions = useMemo(() => {
    const countMap: Record<string, number> = {};
    items.forEach((item) => {
      const cat = (item.data as Record<string, string>).category;
      if (cat) {
        countMap[cat] = (countMap[cat] || 0) + 1;
      }
    });
    // Use category names from category content items
    const names = categoryItems.map((c) => (c.data as Record<string, string>).name).filter(Boolean);
    // Include any product categories not in the category content type
    items.forEach((item) => {
      const cat = (item.data as Record<string, string>).category;
      if (cat && !names.includes(cat)) names.push(cat);
    });
    return names.map((name) => ({
      name,
      count: countMap[name] || 0,
    }));
  }, [items, categoryItems]);

  // Filtered items
  const displayedItems = useMemo(() => {
    if (!filterCategory) return items;
    return items.filter((item) => (item.data as Record<string, string>).category === filterCategory);
  }, [items, filterCategory]);

  // Count of changed items
  const changedCount = useMemo(() => Object.keys(bulkEdits).length, [bulkEdits]);

  const handleDelete = async (contentKey: string, title: string) => {
    if (!confirm(`Delete "${title}"?`)) return;
    try {
      const id = getContentId(contentKey);
      await remove(id);
      addToast('success', 'Product deleted');
    } catch (err) {
      addToast('error', err instanceof Error ? err.message : 'Failed to delete');
    }
  };

  const handleReorder = async (fromIndex: number, toIndex: number) => {
    const newItems = [...items];
    const [moved] = newItems.splice(fromIndex, 1);
    newItems.splice(toIndex, 0, moved);
    const ordered = newItems.map((item, i) => ({
      id: getContentId(item.contentKey),
      sortOrder: i,
    }));
    try {
      await reorder(ordered);
    } catch (err) {
      addToast('error', err instanceof Error ? err.message : 'Failed to reorder');
    }
  };

  const handleBulkFieldChange = useCallback(
    (contentKey: string, field: string, value: string, _originalValue: string) => {
      setBulkEdits((prev) => {
        const existing = prev[contentKey] || {};
        const updated = { ...existing, [field]: value };

        // Get original data for this item to check if we actually changed anything
        const item = items.find((i) => i.contentKey === contentKey);
        if (!item) return prev;
        const originalData = item.data as Record<string, string>;

        // Check each field in the updated edits — remove if same as original
        const cleaned: Record<string, string> = {};
        for (const [k, v] of Object.entries(updated)) {
          const orig = originalData[k] ?? '';
          if (v !== orig) {
            cleaned[k] = v;
          }
        }

        const next = { ...prev };
        if (Object.keys(cleaned).length === 0) {
          delete next[contentKey];
        } else {
          next[contentKey] = cleaned;
        }
        return next;
      });
    },
    [items],
  );

  const handleToggleBulkEdit = () => {
    if (bulkEditMode) {
      // Exiting edit mode — discard changes
      setBulkEdits({});
      setBulkEditMode(false);
    } else {
      setBulkEditMode(true);
    }
  };

  const handleCancelBulkEdit = () => {
    setBulkEdits({});
  };

  const handleSaveBulkEdits = async () => {
    if (!siteId) return;
    const entries = Object.entries(bulkEdits);
    if (entries.length === 0) return;

    setSaving(true);
    setSaveProgress({ current: 0, total: entries.length });
    let successCount = 0;
    let errorCount = 0;

    for (let i = 0; i < entries.length; i++) {
      const [contentKey, changes] = entries[i];
      const item = items.find((it) => it.contentKey === contentKey);
      if (!item) continue;

      const contentId = getContentId(contentKey);
      const mergedData = { ...(item.data as Record<string, unknown>), ...changes };

      setSaveProgress({ current: i + 1, total: entries.length });

      try {
        await api.updateContent(siteId, 'product', contentId, mergedData);
        successCount++;
      } catch {
        errorCount++;
      }
    }

    setSaving(false);
    setBulkEdits({});
    setBulkEditMode(false);
    await refresh();

    if (errorCount > 0) {
      addToast('error', `Saved ${successCount} products, ${errorCount} failed`);
    } else {
      addToast('success', `Saved ${successCount} product${successCount === 1 ? '' : 's'}`);
    }
  };

  const getEditValue = (contentKey: string, field: string, originalValue: string): string => {
    const edits = bulkEdits[contentKey];
    if (edits && field in edits) {
      return (edits as Record<string, string>)[field];
    }
    return originalValue;
  };

  if (loading) {
    return (
      <div className="page">
        <div className="page-header"><h1>Products</h1></div>
        <div className="loading-state"><div className="spinner" /></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page">
        <div className="page-header"><h1>Products</h1></div>
        <div className="error-state"><p>{error}</p></div>
      </div>
    );
  }


  return (
    <div className="page">
      <div className="page-header">
        <h1>Products</h1>
        <div className="page-header-actions">
          <SectionToggle siteId={siteId!} field="showProducts" label="Products" />
          {items.length > 0 && (
            <button
              className={`btn-edit-all${bulkEditMode ? ' active' : ''}`}
              onClick={handleToggleBulkEdit}
              disabled={saving}
            >
              {bulkEditMode ? '✕ Done Editing' : '✏️ Edit All'}
            </button>
          )}
          <Link to={`/sites/${siteId}/content/products/new`} className="btn btn-primary">
            + Add Product
          </Link>
        </div>
      </div>

      <div className="product-list-content">
        <div className="editor-main">
      {categoryOptions.length >= 2 && (
        <div className="list-filter-bar">
          <select
            className="field-input field-select"
            value={filterCategory}
            onChange={(e) => setFilterCategory(e.target.value)}
            style={{ maxWidth: 260 }}
          >
            <option value="">All Categories</option>
            {categoryOptions.map((cat) => (
              <option key={cat.name} value={cat.name}>
                {cat.name} ({cat.count})
              </option>
            ))}
          </select>
        </div>
      )}

      {displayedItems.length === 0 && filterCategory ? (
        <div className="empty-state">
          <div className="empty-icon"><svg width={48} height={48} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" style={{ color: '#ccc' }}><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg></div>
          <h2>No products in &ldquo;{filterCategory}&rdquo;</h2>
          <p>Try selecting a different category or clear the filter.</p>
          <button className="btn btn-primary" onClick={() => setFilterCategory('')}>
            Show All Products
          </button>
        </div>
      ) : items.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon"><svg width={48} height={48} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" style={{ color: '#ccc' }}><line x1="16.5" y1="9.4" x2="7.5" y2="4.21"/><path d="M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 002 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z"/><polyline points="3.27 6.96 12 12.01 20.73 6.96"/><line x1="12" y1="22.08" x2="12" y2="12"/></svg></div>
          <h2>No products yet</h2>
          <p>Add your first product to showcase what you offer.</p>
          <p style={{ fontSize: '0.85rem', color: 'var(--color-gray-500)', marginBottom: 16 }}>
            Products appear on your site as cards with images, descriptions, and prices.
          </p>
          <div className="empty-state-preview">
            <div className="empty-state-preview-card">
              <div className="empty-state-preview-circle" />
              <div className="empty-state-preview-line" />
              <div className="empty-state-preview-line short" />
            </div>
            <div className="empty-state-preview-card">
              <div className="empty-state-preview-circle" />
              <div className="empty-state-preview-line" />
              <div className="empty-state-preview-line short" />
            </div>
            <div className="empty-state-preview-card">
              <div className="empty-state-preview-circle" />
              <div className="empty-state-preview-line" />
              <div className="empty-state-preview-line short" />
            </div>
          </div>
          <Link to={`/sites/${siteId}/content/products/new`} className="btn btn-primary">
            + Add your first product
          </Link>
        </div>
      ) : (
        <DraggableList
          items={displayedItems.map((item) => {
            const data = item.data as Record<string, string>;
            const contentId = getContentId(item.contentKey);
            const isChanged = !!bulkEdits[item.contentKey];
            const itemClasses = [
              bulkEditMode ? 'bulk-edit-mode' : '',
              isChanged ? 'bulk-edit-changed' : '',
            ].filter(Boolean).join(' ');

            return {
              key: item.contentKey,
              className: itemClasses,
              draggable: !bulkEditMode,
              element: (
                <>
                  <div
                    className="list-item-content"
                    onClick={() => {
                      if (!bulkEditMode) {
                        navigate(`/sites/${siteId}/content/products/${contentId}`);
                      }
                    }}
                    style={bulkEditMode ? { cursor: 'default' } : undefined}
                  >
                    <h3 className="list-item-title">{data.title || 'Untitled'}</h3>
                    {bulkEditMode ? (
                      <div className="bulk-edit-fields">
                        <div className="bulk-edit-field">
                          <span className="bulk-edit-label">Price</span>
                          <input
                            type="text"
                            className="bulk-edit-input"
                            value={getEditValue(item.contentKey, 'price', data.price || '')}
                            onChange={(e) =>
                              handleBulkFieldChange(item.contentKey, 'price', e.target.value, data.price || '')
                            }
                            placeholder="e.g. $9.99"
                            onClick={(e) => e.stopPropagation()}
                          />
                        </div>
                        <div className="bulk-edit-field">
                          <span className="bulk-edit-label">Category</span>
                          <select
                            className="bulk-edit-select"
                            value={getEditValue(item.contentKey, 'category', data.category || '')}
                            onChange={(e) =>
                              handleBulkFieldChange(item.contentKey, 'category', e.target.value, data.category || '')
                            }
                            onClick={(e) => e.stopPropagation()}
                          >
                            <option value="">None</option>
                            {categoryOptions.map((cat) => (
                              <option key={cat.name} value={cat.name}>
                                {cat.name}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div className="bulk-edit-field">
                          <label className="bulk-edit-checkbox" onClick={(e) => e.stopPropagation()}>
                            <input
                              type="checkbox"
                              checked={getEditValue(item.contentKey, 'featured', data.featured || '') === 'true'}
                              onChange={(e) =>
                                handleBulkFieldChange(
                                  item.contentKey,
                                  'featured',
                                  e.target.checked ? 'true' : '',
                                  data.featured || '',
                                )
                              }
                            />
                            <span className="bulk-edit-label">Featured</span>
                          </label>
                        </div>
                      </div>
                    ) : (
                      <div className="list-item-meta">
                        {data.category && <span className="tag">{data.category}</span>}
                        {data.price && <span className="list-item-price">{data.price}</span>}
                        {data.featured && <span className="tag tag-accent">Featured</span>}
                      </div>
                    )}
                  </div>
                  {!bulkEditMode && (
                    <div className="list-item-actions">
                      <button
                        className="btn btn-danger btn-sm"
                        onClick={() => handleDelete(item.contentKey, data.title || 'this product')}
                      >
                        Delete
                      </button>
                    </div>
                  )}
                </>
              ),
            };
          })}
          onReorder={handleReorder}
          disabled={bulkEditMode}
        />
      )}

      </div>
      </div>

      {/* Floating save bar */}
      {bulkEditMode && changedCount > 0 && (
        <div className="bulk-save-bar">
          {saving ? (
            <span className="bulk-save-bar-text">
              Saving {saveProgress.current} of {saveProgress.total}…
            </span>
          ) : (
            <>
              <span className="bulk-save-bar-text">
                {changedCount} unsaved change{changedCount === 1 ? '' : 's'}
              </span>
              <button className="btn btn-primary" onClick={handleSaveBulkEdits}>
                Save {changedCount} change{changedCount === 1 ? '' : 's'}
              </button>
              <button className="btn btn-cancel" onClick={handleCancelBulkEdit}>
                Cancel
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}
